import { dealLeadSourceFor } from '../shared/leadAttribution';
import React, { useEffect, useMemo, useState } from 'react';
import { ref, push, update, get } from 'firebase/database';
import { database } from '../firebaseConfig';
import { getAccessible, newOwnership, indexOps, isOwner, ownerOf } from '../services/recordAccess';
import { findContactId } from '../services/pipelineContact';

// Client picker for the new-transaction forms (seller, buyer, landlord, tenant).
// Searches Pipeline records (any type: a Seller can be picked for a Landlord deal) AND Leads/Contacts.
// Who sees what:
//  - Agents and Limited Agents: only records they OWN (shared-with-me records are left out here).
//  - Admin and TC (viewAll): everyone's records, each labeled with the owning agent's name.
// A contact already linked to a listed pipeline record is shown once, as the pipeline record.
const full = (r) => [r.firstName, r.lastName].filter(Boolean).join(' ').trim() || r.name || '';
const typesOf = (r) => (Array.isArray(r.pipelineTypes) && r.pipelineTypes.length ? r.pipelineTypes : [r.kind === 'buyer' ? 'Buyer' : 'Seller']);

export const loadPickerPeople = async (uid, viewAll, names = {}) => {
  if (!uid && !viewAll) return [];
  const [sellers, buyers, contacts] = await Promise.all(['sellerInterests', 'buyerInterests', 'contacts'].map((k) => getAccessible(k, uid, !!viewAll).catch(() => [])));
  const mine = (r) => r && (viewAll || isOwner(r, uid)) && full(r);
  const pipe = [...sellers.map((r) => ({ ...r, kind: 'seller' })), ...buyers.map((r) => ({ ...r, kind: 'buyer' }))].filter(mine);
  const linked = new Set(pipe.map((r) => r.contactId).filter(Boolean));
  const leads = contacts.filter(mine).filter((c) => !linked.has(c.id) && !c.archived).map((c) => ({ ...c, kind: 'contact' }));
  const byId = Object.fromEntries(contacts.map((c) => [c.id, c]));
  const who = (r) => { const o = ownerOf(r); return viewAll && o ? ` - ${names[o] || 'Unknown agent'}` : ''; };
  const reach = (r) => (r.phone ? `, ${r.phone}` : r.email ? `, ${r.email}` : '');
  return [
    ...pipe.map((r) => ({ ...r, group: 'Pipeline', label: `${full(r)} (Pipeline: ${typesOf(r).join(' + ')}${reach(r)})${who(r)}` })),
    ...leads.map((c) => ({ ...c, group: 'Lead', related: Object.keys(c.linkedContactIds || {}).map((id) => ({ ...byId[id], relationship: c.linkedContactIds[id] })).filter((x) => x && full(x)), label: `${full(c)} (Lead${reach(c)})${who(c)}` })),
  ].sort((x, y) => x.label.localeCompare(y.label));
};
// Older name, kept for callers: pipeline people only.
export const loadPipelinePeople = async (uid, isAdmin) => (await loadPickerPeople(uid, isAdmin)).filter((p) => p.group === 'Pipeline');

// The contact for the transaction's client: the linked one, else a visible contact with the same
// email or phone, else a new contact (owned by the person saving). Returns { id } or null.
export const resolveClientContact = async (linked, form, uid, isAdmin, type) => {
  if (linked?.id) return linked;
  const email = String(form.email || '').trim(); const phone = String(form.phone || '').trim();
  const name = [form.firstName, form.lastName].filter(Boolean).join(' ').trim();
  if (!uid || (!email && !phone)) return null;
  try {
    const contacts = Object.fromEntries((await getAccessible('contacts', uid, isAdmin)).map(({ id, ...c }) => [id, c]));
    const hit = findContactId(contacts, { email, phone });
    if (hit) return { id: hit, ...contacts[hit] };
    const r = push(ref(database, 'crm/contacts'));
    const now = new Date().toISOString();
    const rec = { firstName: form.firstName || '', lastName: form.lastName || '', name, email, phone, contactTypes: type ? [type] : [],
      source: `${type || 'Transaction'} transaction`, createdFrom: 'Transaction form', ...newOwnership(uid), ownerType: 'agent', ownerId: uid, assignedToId: uid,
      createdAt: now, updatedAt: now, createdBy: uid };
    await update(ref(database), { [`crm/contacts/${r.key}`]: rec, ...indexOps('contacts', r.key, rec) });
    return { id: r.key, ...rec };
  } catch (err) {
    console.error('Could not find or create the client contact', err);
    return null;
  }
};

const extraOf = (p) => (p.group === 'Lead' ? p.related : p.additionalContacts || []).map((c) => ({ firstName: c.firstName || '', lastName: c.lastName || '', relationship: c.relationship || c.role || '', phone: c.phone || '', email: c.email || '' }));

// viewAll: Admin or TC (search everyone, owner name shown). Everyone else: only their own records.
export default function ClientPicker({ uid, isAdmin, viewAll, onPick, withAddress = true }) {
  const all = !!(viewAll || isAdmin);
  const [people, setPeople] = useState([]);
  const [text, setText] = useState('');
  const [picked, setPicked] = useState(null);
  useEffect(() => {
    if (!uid && !all) return;
    (async () => {
      let names = {};
      if (all) { try { names = Object.fromEntries(Object.entries((await get(ref(database, 'users'))).val() || {}).map(([k, u]) => [k, (u && (u.displayName || u.email)) || ''])); } catch { names = {}; } }
      setPeople(await loadPickerPeople(uid, all, names));
    })().catch(() => setPeople([]));
  }, [uid, all]);
  const byLabel = useMemo(() => new Map(people.map((p) => [p.label, p])), [people]);
  const change = (v) => {
    setText(v);
    const p = byLabel.get(v);
    if (!p) return;
    setPicked(p);
    const lead = p.group === 'Lead';
    const city = p.city || p.interestedCity || '';
    onPick({
      // Pipeline picks mark that record converted; lead picks just link the contact.
      source: lead ? null : { kind: p.kind, id: p.id },
      contactId: lead ? p.id : (p.contactId || null),
      contact: lead ? { id: p.id, ...p } : null,
      additionalContacts: extraOf(p),
      fields: {
        firstName: p.firstName || '', lastName: p.lastName || '', email: p.email || '', phone: p.phone || '',
        ...(p.source ? { source: p.source } : {}), ...(city ? { city } : {}),
        // Lead generated by (agent/team) on the lead carries onto the deal's lead source.
        ...(dealLeadSourceFor(p.leadGeneratedBy) ? { leadSource: dealLeadSourceFor(p.leadGeneratedBy) } : {}),
        ...(withAddress && p.kind !== 'buyer' && p.propertyAddress ? { propertyAddress: p.propertyAddress } : {}),
      },
    });
  };
  return (
    <div className="client-picker-box" style={{ marginBottom: 16, padding: 12, border: '1px solid #D4AF37', borderRadius: 8, background: '#FFFDF6', color: '#111', colorScheme: 'light', maxWidth: '100%', boxSizing: 'border-box' }}>
      <style>{'.client-picker-box #client-picker::placeholder{color:#555;opacity:1}.client-picker-box #client-picker:focus{outline:2px solid #D4AF37;outline-offset:1px}'}</style>
      <label htmlFor="client-picker" style={{ display: 'block', fontWeight: 700, fontSize: 14, marginBottom: 6, color: '#111' }}>Client from Pipeline or Leads (optional)</label>
      <input id="client-picker" list="client-picker-options" value={text} onChange={(e) => change(e.target.value)} placeholder="Start typing a name, phone or email"
        style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 6, border: '1px solid #b8a67a', fontSize: 16, color: '#111', background: '#fff', WebkitTextFillColor: '#111', colorScheme: 'light' }} />
      <datalist id="client-picker-options">{people.map((p) => <option key={`${p.kind}-${p.id}`} value={p.label} />)}</datalist>
      <div style={{ fontSize: 12, color: '#333', marginTop: 6 }}>
        {picked ? `Filled in from ${full(picked)}'s ${picked.group === 'Lead' ? 'lead' : 'pipeline record'}. You can still change anything below.` : `${all ? "Searches everyone's" : 'Shows your'} Pipeline and Leads. New person? Just type their details below; a contact is created for them when you save.`}
      </div>
    </div>
  );
}
